import {
  doc,
  deleteField,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import type { UserProfile, UserPreferences, GraphStats, UserFeatureFlags } from '../types';

// ─── Path Helper ─────────────────────────────────────────────────────────────

const userDoc = (userId: string) => doc(getFirebaseDb(), 'users', userId);

// ─── Profile ─────────────────────────────────────────────────────────────────

export async function createProfile(
  userId: string,
  data: {
    displayName: string;
    email: string;
    preferences?: Partial<UserPreferences>;
    features?: Partial<UserFeatureFlags>;
  }
): Promise<void> {
  const defaultPreferences: UserPreferences = {
    morningCheckInTime: '07:30',
    eveningCheckInTime: '20:30',
    preferredModality: 'text',
    timezone: 'Europe/London',
    notificationsEnabled: true,
    preferredLanguage: 'en',
    theme: 'system',
    voiceProvider: 'openai',
    voiceModel: 'gpt-realtime-2',
    voiceName: 'alloy',
    voicePauseTiming: {
      checkIns: 'short',
      taskPlanning: 'very_short',
      coaching: 'long',
    },
    voiceGenderPreference: 'any',
    phoneNumberVerified: false,
    phoneCallsEnabled: false,
    showMoodEnergyMorning: true,
    showMoodEnergyEvening: true,
    askMoodEnergyPhone: false,
    scheduledCallTypes: ['morning_checkin', 'evening_checkin'],
    scheduledCallCadence: 'weekdays',
  };

  const defaultStats: GraphStats = {
    nodeCount: 0,
    edgeCount: 0,
    motifCount: 0,
    totalSessions: 0,
  };

  const defaultFeatures: UserFeatureFlags = {
    controlCentre: false,
  };

  const profile: Omit<UserProfile, 'userId'> = {
    displayName: data.displayName,
    preferredName: data.displayName.split(' ')[0] || data.displayName,
    email: data.email,
    createdAt: Timestamp.now(),
    onboardingComplete: false,
    features: { ...defaultFeatures, ...data.features },
    preferences: { ...defaultPreferences, ...data.preferences },
    graphStats: defaultStats,
  };

  await setDoc(userDoc(userId), profile);
}

export async function getProfile(userId: string): Promise<UserProfile | null> {
  const snap = await getDoc(userDoc(userId));
  if (!snap.exists()) return null;
  return { userId: snap.id, ...snap.data() } as UserProfile;
}

export async function updateProfile(
  userId: string,
  updates: Partial<Omit<UserProfile, 'userId' | 'createdAt'>>
): Promise<void> {
  await updateDoc(userDoc(userId), updates);
}

export async function updatePreferences(
  userId: string,
  preferences: Partial<UserPreferences>
): Promise<void> {
  // Firestore dot-notation merge for nested fields
  const updates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(preferences)) {
    // Firestore does not accept undefined; use deleteField to clear optional values.
    updates[`preferences.${key}`] = value === undefined ? deleteField() : value;
  }
  await updateDoc(userDoc(userId), updates);
}

export async function updateFeatureFlags(
  userId: string,
  features: Partial<UserFeatureFlags>
): Promise<void> {
  const updates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(features)) {
    updates[`features.${key}`] = value === undefined ? deleteField() : value;
  }
  await updateDoc(userDoc(userId), updates);
}

export async function updateGraphStats(userId: string, stats: Partial<GraphStats>): Promise<void> {
  const updates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(stats)) {
    updates[`graphStats.${key}`] = value;
  }
  await updateDoc(userDoc(userId), updates);
}

export async function markOnboardingComplete(userId: string): Promise<void> {
  await updateDoc(userDoc(userId), {
    onboardingComplete: true,
    onboardingCompletedAt: Timestamp.now(),
  });
}
